import { useEffect, useState } from "react";
import api from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { YEAR_COLORS } from "@/components/dashboard/TrendsSection";
import { exportCSV, exportChartPNG } from "@/lib/exports";
import { ExportBtn } from "@/components/dashboard/ExportBtn";

const CHANNEL_COLORS = { B2C: "#10B981", B2B: "#3B82F6", Other: "#64748B" };
const tooltipStyle = { backgroundColor: "#111827", border: "1px solid #334155", borderRadius: 8, color: "#F8FAFC", fontSize: 12 };
const channelBadge = (c) => c === "B2C" ? "border-emerald-500/50 text-emerald-400" : c === "B2B" ? "border-blue-500/50 text-blue-400" : "border-slate-500/50 text-slate-400";

export const SalesAnalysis = ({ fq }) => {
  const [data, setData] = useState(null);
  const [pq, setPq] = useState("");
  const [year, setYear] = useState(null);
  const [party, setParty] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams();
    if (year) params.set("year", year);
    if (party) params.set("party", party);
    if (fq) params.set("filters", decodeURIComponent(fq));
    api.get(`/analysis/sales?${params.toString()}`).then((r) => {
      setData(r.data);
      if (!year) setYear(r.data.year);
      if (!party && r.data.party_monthly) setParty(r.data.party_monthly.party);
    }).catch(() => {});
  }, [year, party, fq]);

  if (!data) return <div className="h-96 bg-[#1E293B] border border-[#334155] rounded-lg animate-pulse" />;
  const totals = data.channel_monthly.reduce((a, m) => ({ B2C: a.B2C + m.B2C, B2B: a.B2B + m.B2B, Other: a.Other + m.Other }), { B2C: 0, B2B: 0, Other: 0 });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs uppercase tracking-widest text-slate-400">Year</label>
        <Select value={data.year || ""} onValueChange={setYear}>
          <SelectTrigger data-testid="sales-year-select" className="w-32 bg-[#0B0F17] border-[#334155] text-slate-100">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#111827] border-[#334155] text-slate-100">
            {data.years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <span data-testid="channel-totals" className="text-xs text-slate-400">
          <b className="text-emerald-400">{totals.B2C}</b> retail (B2C) · <b className="text-blue-400">{totals.B2B}</b> subdealer (B2B) · <b className="text-slate-300">{totals.Other}</b> other in {data.year}
        </span>
      </div>
      <div data-testid="channel-monthly-chart" className="bg-[#1E293B] border border-[#334155] rounded-lg p-5">
        <h3 className="text-lg font-semibold mb-1 flex items-center justify-between" style={{ fontFamily: "Outfit, sans-serif" }}>
          <span>Monthly Deliveries by Channel — {data.year} <span className="text-sm text-slate-400 font-normal">(stacked)</span></span>
          <ExportBtn onCSV={() => exportCSV(data.channel_monthly, `channel-monthly-${data.year}`)} onPNG={() => exportChartPNG("channel-monthly-chart", `channel-monthly-${data.year}`)} />
        </h3>
        <p className="text-xs text-slate-400 mb-3">Counter Customer = retail B2C · ASC parties = your subdealers B2B.</p>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data.channel_monthly}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="month" tick={{ fill: "#94A3B8", fontSize: 11 }} />
            <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} />
            <Tooltip contentStyle={{ backgroundColor: "#111827", border: "1px solid #334155", borderRadius: 8, color: "#F8FAFC", fontSize: 12 }} cursor={{ fill: "#334155", opacity: 0.3 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="B2C" stackId="c" fill={CHANNEL_COLORS.B2C} name="Retail (B2C)" />
            <Bar dataKey="B2B" stackId="c" fill={CHANNEL_COLORS.B2B} name="Subdealer (B2B)" />
            <Bar dataKey="Other" stackId="c" fill={CHANNEL_COLORS.Other} name="Other" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {data.party_monthly && (
        <div data-testid="party-monthly-chart" className="bg-[#1E293B] border border-[#334155] rounded-lg p-5">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
            <h3 className="text-lg font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>
              Party Deliveries — Same Month, All Years <span className="text-sm text-slate-400 font-normal">(stacked)</span>
            </h3>
            <span className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <button data-testid="party-monthly-select" className="h-9 px-3 rounded-md border border-[#334155] bg-[#0B0F17] text-slate-100 text-xs flex items-center gap-1">
                    {data.party_monthly.selected.length} part{data.party_monthly.selected.length === 1 ? "y" : "ies"} <ChevronDown className="h-3 w-3" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-2 bg-[#111827] border-[#334155] text-slate-100" align="end">
                  <Input placeholder="Search party…" value={pq} onChange={(e) => setPq(e.target.value)}
                    className="bg-[#0B0F17] border-[#334155] text-slate-100 h-8 text-xs mb-1.5" />
                  <div className="flex gap-1 mb-1.5">
                    <button data-testid="party-select-all" onClick={() => setParty((data.all_parties || []).join("||"))}
                      className="flex-1 text-[10px] py-1 rounded border border-blue-500/40 text-blue-400 hover:bg-blue-500/10">Select all</button>
                    <button data-testid="party-deselect-all" onClick={() => setParty("")}
                      className="flex-1 text-[10px] py-1 rounded border border-slate-700 text-slate-400 hover:bg-slate-800">Deselect all</button>
                  </div>
                  <div className="max-h-56 overflow-auto">
                  {(data.all_parties || []).filter((p) => !pq || p.toLowerCase().includes(pq.toLowerCase())).map((p) => (
                    <label key={p} className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-slate-800 cursor-pointer text-xs">
                      <Checkbox checked={data.party_monthly.selected.includes(p)}
                        onCheckedChange={() => {
                          const cur = data.party_monthly.selected;
                          const next = cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p];
                          setParty(next.join("||"));
                        }}
                        className="border-slate-600 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600" />
                      <span className="truncate">{p}</span>
                    </label>
                  ))}
                  </div>
                </PopoverContent>
              </Popover>
              <ExportBtn onCSV={() => exportCSV(data.party_monthly.data, "party-monthly")} onPNG={() => exportChartPNG("party-monthly-chart", "party-monthly")} />
            </span>
          </div>
          <p className="text-xs text-slate-400 mb-3">e.g. every September for <b className="text-slate-300">{data.party_monthly.party}</b> stacked across {data.party_monthly.years[0]}–{data.party_monthly.years[data.party_monthly.years.length - 1]}.</p>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.party_monthly.data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="month" tick={{ fill: "#94A3B8", fontSize: 11 }} />
              <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#334155", opacity: 0.3 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {data.party_monthly.years.map((y, i) => (
                <Bar key={y} dataKey={y} stackId="pm" fill={YEAR_COLORS[i % YEAR_COLORS.length]} name={y}
                  radius={i === data.party_monthly.years.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      <div data-testid="party-year-table" className="bg-[#1E293B] border border-[#334155] rounded-lg p-5">
        <h3 className="text-lg font-semibold mb-3 flex items-center justify-between" style={{ fontFamily: "Outfit, sans-serif" }}>
          <span>Party-wise Deliveries — Year by Year</span>
          <ExportBtn onCSV={() => exportCSV(data.party_table, "party-wise-yearly")} />
        </h3>
        <div className="overflow-auto max-h-[480px]">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="sticky top-0 bg-[#1E293B]">
              <tr className="text-left text-xs uppercase tracking-widest text-slate-400 border-b border-[#334155]">
                <th className="pb-2 pr-3">Party</th>
                <th className="pb-2 pr-3">Channel</th>
                {data.years.map((y) => <th key={y} className="pb-2 pr-3 text-right">{y}</th>)}
                <th className="pb-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.party_table.map((p, i) => (
                <tr key={p.party} data-testid={`party-year-row-${i}`} className="border-b border-slate-800/60 hover:bg-slate-800/40 transition-colors">
                  <td className="py-2 pr-3 text-slate-200">{p.party}</td>
                  <td className="py-2 pr-3"><Badge variant="outline" className={`text-[10px] ${channelBadge(p.channel)}`}>{p.channel}</Badge></td>
                  {data.years.map((y) => <td key={y} className="py-2 pr-3 text-right font-mono text-slate-300">{p.by_year[y] || "—"}</td>)}
                  <td className="py-2 text-right font-mono font-bold text-emerald-400">{p.total}</td>
                </tr>
              ))}
              <tr data-testid="party-year-total-row" className="border-t-2 border-slate-600 font-bold">
                <td className="py-2.5 pr-3 text-slate-100">TOTAL</td><td></td>
                {data.years.map((y) => <td key={y} className="py-2.5 pr-3 text-right font-mono text-slate-100">{data.party_table.reduce((t, p) => t + (p.by_year[y] || 0), 0)}</td>)}
                <td className="py-2.5 text-right font-mono text-emerald-400">{data.party_table.reduce((t, p) => t + p.total, 0)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
